<template>
  <material-card
    :title="$t('components.vuematerialdashboard.core.appbar.claims_payments')"
    :color="$vuetify.theme.themes.light.secondary"
    icon="attach_money"
  >
    <v-container fluid v-if="options">
      <v-form>
        <v-row>
          <v-col>
            <v-autocomplete
              name="plans"
              :menu-props="{ contentClass: 'text-left benefits' }"
              dense
              :label="$t('globals.plan')"
              v-model="plan"
              :items="plans"
              :item-text="planDescription"
              item-value="Id"
              hide-details
              clearable
              return-object
              :rules="[$rules.required]"
              class="required"
              outlined
            ></v-autocomplete>
          </v-col>
          <v-col>
            <trans-enum
              :value.sync="searchPaymentMethod"
              :items="options.Enums.PaymentMethod"
              :label="$t('headers.claims_payments.PaymentMethod')"
              outlined
              dense
            ></trans-enum>
          </v-col>
        </v-row>
      </v-form>
      <div style="margin-right: 20px; margin-top: 10px" v-show="plan">
        <v-menu bottom left>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" color="primary" dark>
              <v-icon>more_vert</v-icon>
              {{
                $t("components.claims.claims_payments_table.create_new_batch")
              }}
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="newBatch('DirectDeposit')">
              <v-list-item-title>
                {{ $t("components.memberview.direct_deposit") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="newBatch('Cheque')">
              <v-list-item-title>
                {{ $t("enums.Cheque") }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </v-container>
    <div v-else>
      <v-row justify="center">
        <v-progress-circular
          :size="70"
          :width="7"
          color="accent"
          indeterminate
        ></v-progress-circular>
      </v-row>
    </div>
    <div v-show="plan">
      <pbas-data-table
        ref="table"
        :dataProvider="provider"
        :rowActions="rowActions"
        :expandedRowActions="expandedRowActions"
        :exporters="$exporters"
        @error="error = $event"
        :showSelect="false"
        :canExpand="(item) => true"
        :showExpand="true"
        :generateProviderForExpanded="(item) => generatePaymentsProvider(item)"
        @expanded-changed="setData($event)"
      ></pbas-data-table>
    </div>
    <v-dialog scrollable persistent v-model="batchDialog">
      <material-card
        v-if="batchDialog"
        :title="
          $t(
            'components.claims.claims_payments_table.batch_title_{0}_{1}_{2}',
            [paymentMethod, plan.UniqueIdentifier, getPlanName(plan)]
          )
        "
        :color="$vuetify.theme.themes.light.primary"
        icon="done"
      >
        <v-card-text>
          <batch-component
            :paymentMethod="paymentMethod"
            :plan="plan"
            @close="closeBatch"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="cancelBatchDialog = true">
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="cancelBatchDialog" persistent max-width="400">
      <material-card
        :title="$t('components.claims.claims_payments_table.cancel_payment')"
        :color="$vuetify.theme.themes.light.error"
        icon="cancel"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="cancelBatchDialog = false">
            {{ $t("components.claim_submission.keep_editing") }}
          </v-btn>
          <v-btn color="primary" text @click="closeBatch">
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog
      scrollable
      persistent
      v-model="reviewSubmissionDialog"
      max-width="1800"
    >
      <material-card
        v-if="reviewSubmissionDialog && claimSubmission"
        :title="
          $t(
            'components.claims.submitted_submissions_table.review_claim_submission_title_{0}',
            [claimSubmission.Identifier]
          )
        "
        :color="$vuetify.theme.themes.light.primary"
        icon="edit"
      >
        <v-card-text>
          <claim-submission-tabs
            :claimSubmission="claimSubmission"
            :memberIdentity="memberIdentity"
            :patients="patients"
            @reload-member-identity="loadMemberDetails"
            :planName="translate2(planIdentity, 'Name')"
            :planNumber="planIdentity.UniqueIdentifier"
            :submitted="true"
            :review="true"
            @reload-claim-submission="loadClaimSubmission(claimSubmission.Id)"
            :planStartDate="planIdentity.FirstRenewalDate"
            @close="closeClaim"
            :hasHSA="planIdentity.HasHSA"
            :planIdentityId="planIdentity.Id"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="cancelEditDialog = true">
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="cancelEditDialog" persistent max-width="400">
      <material-card
        :title="$t('components.claim_submission.close_dialog_title')"
        :color="$vuetify.theme.themes.light.error"
        icon="cancel"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="cancelEditDialog = false">
            {{ $t("components.claim_submission.keep_editing") }}
          </v-btn>
          <v-btn color="primary" text @click="closeClaim">
            {{ $t("globals.close") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="loadingSubmission" persistent max-width="200">
      <v-card>
        <v-card-title>{{ $t("globals.please_wait") }}</v-card-title>
        <v-card-text>
          <v-container fluid grid-list-md>
            <v-layout row justify-center>
              <v-flex xs12 sm6 md4 lg3>
                <v-progress-circular indeterminate></v-progress-circular>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog scrollable v-model="loading" persistent max-width="200">
      <v-card>
        <v-card-title>{{ $t("globals.please_wait") }}</v-card-title>
        <v-card-text>
          <v-container fluid grid-list-md>
            <v-layout row justify-center>
              <v-flex xs12 sm6 md4 lg3>
                <v-progress-circular indeterminate></v-progress-circular>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <cancel-payment-dialog
      :model="cancellationDialog"
      :payment="selectedPayment"
      @cancel="closeCancellation"
      @success="onSuccessfulCancellation"
    ></cancel-payment-dialog>
    <error-handler :error="error" />
  </material-card>
</template>

<script>
import getBatchPaymentsHeaders from "../../headers/BatchPayments";
import getClaimsPaymentsHeaders from "../../headers/ClaimsPayments";
import { ODataProvider } from "pbas-vue-components";
import { mapActions, mapGetters } from "vuex";
import ErrorHandler from "../../components/ErrorHandler";
import { translate2 } from "@/trans2";
import BatchComponent from "./BatchComponent.vue";
import TransEnum from "@/components/TransEnum.vue";
import { saveAs } from "file-saver";
import ClaimSubmissionTabs from "./ClaimSubmissionTabs.vue";
import CancelPaymentDialog from "./CancelPaymentDialog.vue";

export default {
  components: {
    ErrorHandler,
    BatchComponent,
    TransEnum,
    ClaimSubmissionTabs,
    CancelPaymentDialog,
  },
  data: function () {
    return {
      provider: undefined,
      error: null,
      rowActions: [
        {
          name: "download",
          icon: "file_download",
          title: "components.claims.claims_payments_table.download_file",
          handler: (item) => this.downloadFile(item),
          visible: (item) => item.FileId !== null && item.FileId !== undefined,
        },
        {
          name: "export",
          icon: "file_download",
          title: "components.claims.claims_payments_table.export_file",
          handler: (item) => this.exportBatchFileHandler(item),
          visible: (item) => item.FileId === null || item.FileId === undefined,
        },
      ],
      expandedRowActions: [
        {
          name: "cancel",
          icon: "delete",
          title:
            "components.claims.claims_payments_table.cancel_payment_action",
          handler: (item) => this.cancelPaymentHandler(item),
          visible: (item) =>
            item.CancelledAt === null || item.CancelledAt === undefined,
        },
      ],
      plan: null,
      paymentMethod: null,
      batchDialog: false,
      cancelBatchDialog: false,
      headers: [],
      searchPaymentMethod: null,
      paymentsHeaders: [],
      batchPayments: {},
      reviewSubmissionDialog: false,
      patients: [],
      claimSubmission: null,
      cancelEditDialog: false,
      loadingSubmission: false,
      selectedPayment: null,
      cancellationDialog: false,
      loading: false,
      btnListeners: [],
    };
  },
  computed: {
    ...mapGetters("common", ["plans", "settings", "claimsPlan"]),
    ...mapGetters("plansMembers", ["options"]),
    ...mapGetters("common", { selectedPlan: "plan" }),
  },
  mounted() {
    if (this.options) {
      this.headers = getBatchPaymentsHeaders(this.options.Enums);
      this.paymentsHeaders = getClaimsPaymentsHeaders(this.options.Enums, true);
      this.loadUserPlans();
    }
  },
  beforeDestroy() {
    this.btnListeners.forEach(({ btn, listener }) => {
      btn.removeEventListener("click", listener);
    });
  },
  watch: {
    plan: {
      handler(v) {
        if (v) {
          this.provider = undefined;
          this.$nextTick(() => {
            this.loadPlanData();
          });
        }
        this.setClaimsPlan(v);
      },
    },
    searchPaymentMethod: {
      handler(v) {
        if (v) {
          this.provider = undefined;
          this.$nextTick(() => {
            this.loadPlanData();
          });
        }
      },
    },
    options: {
      handler(v) {
        if (v) {
          this.headers = getBatchPaymentsHeaders(this.options.Enums);
          this.paymentsHeaders = getClaimsPaymentsHeaders(
            this.options.Enums,
            true
          );
          this.loadUserPlans();
        }
      },
    },
  },
  methods: {
    ...mapActions("auth", ["login", "silentRefresh"]),
    ...mapActions("plansMembers", ["getUserPlans", "getPlan", "getMemberInfo"]),
    ...mapActions("common", ["setPlans", "setClaimsPlan"]),
    ...mapActions("documentsService", ["getFile"]),
    ...mapActions("claims", [
      "exportBatchFile",
      "getClaimSubmissionById",
      "getPlanBenefitById",
      "getDrugByDIN",
      "getProviderById",
    ]),
    planDescription(plan) {
      const desc = translate2(plan, "Name");
      if (desc.indexOf(plan.UniqueIdentifier) === 0) {
        return desc;
      }
      return plan.UniqueIdentifier + "-" + desc;
    },
    closeClaim() {
      this.cancelEditDialog = false;
      this.reviewSubmissionDialog = false;
      this.claimSubmission = null;
      // this.$emit("reload");
    },
    translate2(item, field) {
      return translate2(item, field);
    },
    generatePaymentsProvider(item) {
      const p = new ODataProvider(
        this.$axiosHttp,
        `${this.settings.APIURL}plans/${this.plan.Id}/claim-payments`,
        this.paymentsHeaders
      );
      p.filter = `BatchId eq ${item.Id}`;
      return p;
    },
    async loadUserPlans() {
      try {
        var usr = await this.silentRefresh();
        if (usr) {
          let plans = (await this.getUserPlans()).data.value;
          this.setPlans(
            plans.map((o) => {
              o.Name = o.Current.Name;
              o.Description = o.Current.Description;
              o.Translations = o.Current.Translations;
              return o;
            })
          );
          if (this.claimsPlan !== null && this.claimsPlan !== undefined) {
            this.plan = this.claimsPlan;
          } else {
            this.plan = this.selectedPlan;
          }
        }
      } catch (error) {
        //console.log("selectPlan: silent refresh error: ", error);
        this.login();
      }
    },
    getPlanName(item) {
      // console.log(item);
      return translate2(item, "Name");
    },
    edit(item) {
      this.editdrug = item;
    },
    loadPlanData() {
      // if (this.provider === undefined) {
      // console.log(this.headers);
      this.provider = new ODataProvider(
        this.$axiosHttp,
        `${this.settings.APIURL}plans/${this.plan.Id}/batch-payments`,
        this.headers
      );
      if (
        this.searchPaymentMethod !== null &&
        this.searchPaymentMethod !== undefined
      ) {
        this.provider.filter = `PaymentMethod eq '${this.searchPaymentMethod}'`;
      }
      // } else {
      //   this.$refs.table.getDataAsync();
      // }
    },
    newBatch(paymentMethod) {
      this.paymentMethod = paymentMethod;
      this.batchDialog = true;
      // this.$router.push({ name: "claimSubmission", params: { paymentMethod: paymentMethod } });
    },
    closeBatch() {
      this.paymentMethod = null;
      this.cancelBatchDialog = false;
      this.batchDialog = false;
      this.$refs.table.getDataAsync();
    },
    async downloadFile(item) {
      try {
        this.loading = true;

        const file = (await this.getFile(item.FileId)).data;
        const blob = this.$files.b64toBlob(file);
        let name = item.FileName;
        if (name === null || name === undefined) {
          name = "batchFile.txt";
        }
        saveAs(blob, name);

        if (
          item.ChequeTemplateFileId != null &&
          item.ChequeTemplateFileId !== undefined
        ) {
          const file = (await this.getFile(item.ChequeTemplateFileId)).data;
          const blob = this.$files.b64toBlob(file);
          name = item.ChequeTemplateFileName;
          if (name === null || name === undefined) {
            name = "batchFile.txt";
          }
          saveAs(blob, name);
        }
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    async exportBatchFileHandler(item) {
      try {
        this.loadingSubmission = true;
        const data = (
          await this.exportBatchFile({
            planIdentityId: this.plan.Id,
            batchId: item.Id,
          })
        ).data;
        for (const f of data) {
          const fileId = f.Id;
          const fileName = f.Name;

          const file = (await this.getFile(fileId)).data;
          const blob = this.$files.b64toBlob(file);
          saveAs(blob, fileName);
        }
        this.$refs.table.getDataAsync();
      } catch (error) {
        this.error = error;
      } finally {
        this.loadingSubmission = false;
      }
    },
    cancelPaymentHandler(item) {
      console.log(item);
      this.selectedPayment = item;
      this.cancellationDialog = true;
    },
    closeCancellation() {
      this.cancellationDialog = false;
      this.selectedPayment = null;
    },
    onSuccessfulCancellation() {
      this.closeCancellation();
      this.$refs.table.getDataAsync();
    },
    setData(data) {
      this.batchPayments[data.parent.Id] = data.items;
      this.$nextTick(() => {
        for (let payment of data.items) {
          // console.log(payment);
          let btn = document.getElementById(payment.ClaimSubmission.Identifier);
          // console.log(btn);
          const listener = () =>
            this.reviewClaimSubmission({
              submissionId: payment.ClaimSubmission.Id,
              memberId: payment.ClaimSubmission.MemberId,
            });
          btn.addEventListener("click", listener, false);
          this.btnListeners.push({ btn, listener });
        }
      });
    },
    async reviewClaimSubmission({ submissionId, memberId }) {
      try {
        this.loadingSubmission = true;
        // this.item = JSON.parse(JSON.stringify(item));
        // this.submissionId = item.Identifier;

        await this.loadPlanDetails(this.plan.Id);
        await this.loadMemberDetails(memberId, this.plan.Id);
        await this.loadClaimSubmission(submissionId);

        this.reviewSubmissionDialog = true;
      } catch (error) {
        this.error = error;
      } finally {
        this.loadingSubmission = false;
      }
    },
    async loadClaimSubmission(claimSubmissionId) {
      // console.log("load claim submission", claimSubmissionId);
      this.loading = true;
      try {
        let item = (
          await this.getClaimSubmissionById({
            id: claimSubmissionId,
          })
        ).data;
        for (let claim of item.Claims) {
          claim.Identifier = Math.random().toString();
          if (claim.DependantId) {
            claim.Patient = this.patients.filter(
              (o) => o.IdentityId === claim.DependantId
            )[0];
          } else {
            claim.Patient = this.patients.filter(
              (o) => o.Id === claim.MemberId
            )[0];
          }
          claim.PlanBenefit = (
            await this.getPlanBenefitById({
              id: claim.PlanBenefitId,
            })
          ).data.value[0];

          // console.log(JSON.stringify(claim));
          // let num = parseFloat(claim.ExpenseAmount);
          // console.log(num);
          // if (!isNaN(num)) {
          //   console.log(num.toFixed(2))
          //   claim.ExpenseAmount = num.toFixed(2);
          // } else {
          //   claim.ExpenseAmount = "0.00";
          // }
          // num = parseFloat(claim.COBAmount);
          // if (!isNaN(num)) {
          //   claim.COBAmount = num.toFixed(2);
          // } else {
          //   claim.ExpenseAmount = "0.00";
          // }
          // num = parseFloat(claim.LabFee);
          // if (!isNaN(num)) {
          //   claim.LabFee = num.toFixed(2);
          // } else {
          //   claim.ExpenseAmount = "0.00";
          // }
          if (claim.ToothCodeId !== null && claim.ToothCodeId !== undefined) {
            // console.log("claim has tooth code id: ", claim.ToothCodeId);
            claim.ToothCode = this.options.Models.ToothCode.filter(
              (o) => o.Id === claim.ToothCodeId
            )[0];
            // console.log(claim.ToothCode);
            // console.log(this.options.Models.ToothCode);
          }
          if (claim.DIN !== null && claim.DIN !== undefined) {
            claim.DIN = (
              await this.getDrugByDIN({
                planIdentityId: claim.PlanId,
                din: claim.DIN,
              })
            ).data.value[0];
          }
        }
        for (let proof of item.Proofs) {
          proof.Identifier = Math.random().toString();
        }
        for (let note of item.Notes) {
          note.Identifier = Math.random().toString();
        }
        item.Notes.unshift({
          Identifier: Math.random().toString(),
          Writer: this.user,
        });
        if (item.ProviderId) {
          item.Provider = (
            await this.getProviderById({
              id: item.ProviderId,
            })
          ).data.value[0];
        }
        item.Patient = item.Claims[0].Patient;
        item.Id = claimSubmissionId;
        this.claimSubmission = item;
        // console.log(JSON.stringify(item));
      } catch (error) {
        // console.log(error);
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    async loadMemberDetails(memberIdentityId, planIdentityId) {
      // this.$emit("plan-name", this.getPlanName(this.searchForm.planIdentity));
      // this.$emit("plan-number", this.getPlanNumber(this.searchForm.planIdentity));
      // this.loading = true;
      try {
        //CurrentMemberBenefits($expand=CoverageIdentity($expand=Current($expand=Translations($select=LanguageCode,Name)))),
        this.memberIdentity = (
          await this.getMemberInfo({
            planIdentityId: planIdentityId,
            memberIdentityId: memberIdentityId,
            odataQuery:
              "$select=Id,Email,PlanIdentityId,PlanEntryDate,TerminatedAt&$expand=CurrentMembershipStatus($expand=MemberGroupIdentity($select=Id)),MemberIdentification($select=Certificate),CurrentBankAccount,CurrentMember($select=LastName,FirstName,MiddleName,DateOfBirth),CurrentDependants($select=Id,IdentityId,FirstName,MiddleName,LastName,DateOfBirth;$expand=Relationship($expand=Translations),Identity($select=Code,IsEligible)),CurrentContactInformation($expand=Address($expand=Country($expand=Translations),Province($expand=Translations)))",
          })
        ).data;

        this.getPatients();
        // this.$emit("select", this.memberIdentity);
      } catch (error) {
        this.error = error;
      } finally {
        // this.loading = false;
      }
    },
    async loadPlanDetails(planIdentityId) {
      // this.$emit("plan-name", this.getPlanName(this.searchForm.planIdentity));
      // this.$emit("plan-number", this.getPlanNumber(this.searchForm.planIdentity));
      // this.loading = true;
      try {
        //CurrentMemberBenefits($expand=CoverageIdentity($expand=Current($expand=Translations($select=LanguageCode,Name)))),
        this.planIdentity = (await this.getPlan(planIdentityId)).data;

        // this.$emit("select", this.memberIdentity);
      } catch (error) {
        this.error = error;
      } finally {
        // this.loading = false;
      }
    },
    getPatients() {
      this.patients = this.memberIdentity.CurrentDependants.sort((a, b) => {
        if (Date.parse(a.DateOfBirth) < Date.parse(b.DateOfBirth)) {
          return -1;
        } else if (Date.parse(a.DateOfBirth) === Date.parse(b.DateOfBirth)) {
          return 0;
        } else {
          return 1;
        }
      });
      this.patients.unshift({
        Id: this.memberIdentity.Id,
        FirstName: this.memberIdentity.CurrentMember.FirstName,
        LastName: this.memberIdentity.CurrentMember.LastName,
        MiddleName: this.memberIdentity.CurrentMember.MiddleName,
        DateOfBirth: this.memberIdentity.CurrentMember.DateOfBirth,
      });
    },
  },
};
</script>
